import { useCallback, useState } from 'react';
import type { DraggableWorksheetFormComponent } from '@packages/models/api';
import { useWorksheetUploadGuard } from './use-worksheet-upload-guard';
import { usePrompt } from '../../../../apps/web/src/vin-sessions/components/stis-publications/hooks/use-prompt';

// Define element types that can contain file uploads
type ElementWithFileUpload =
    | {
          formElementFile?: { status?: string };
          options?: Array<{ optionFile?: { status?: string } }>;
          optionConditionsElements?: ElementWithFileUpload[];
      }
    | {
          formElementFile?: { status?: string };
          options?: Array<{ optionFile?: { status?: string } }>;
      };

/**
 * Hook that provides comprehensive navigation protection during worksheet attachment uploads
 *
 * This hook:
 * - Detects when worksheet attachments are uploading
 * - Shows browser confirmation dialog when user tries to close/refresh tab
 * - Shows confirmation dialog when user tries to navigate within the app
 * - Provides methods to handle user confirmation
 */
export function useWorksheetNavigationGuard(elements?: (DraggableWorksheetFormComponent | ElementWithFileUpload)[]) {
    const [showNavigationModal, setShowNavigationModal] = useState(false);
    const { hasUploadsInProgress, warningMessage } = useWorksheetUploadGuard(elements);

    // Use the existing usePrompt hook for internal navigation protection
    const { retryNav, clearRetryNav } = usePrompt(
        'A file upload is currently in progress. Leaving this page may cancel the upload and cause your worksheet changes to be lost.',
        hasUploadsInProgress
    );

    /**
     * Handle when user confirms they want to leave despite upload in progress
     */
    const handleConfirmNavigation = useCallback(() => {
        setShowNavigationModal(false);
        if (retryNav) {
            retryNav();
            clearRetryNav();
        }
    }, [retryNav, clearRetryNav]);

    /**
     * Handle when user cancels navigation and wants to stay
     */
    const handleCancelNavigation = useCallback(() => {
        setShowNavigationModal(false);
        clearRetryNav();
    }, [clearRetryNav]);

    /**
     * Show confirmation modal when navigation is attempted
     */
    const showConfirmationModal = useCallback(() => {
        if (hasUploadsInProgress && retryNav) {
            setShowNavigationModal(true);
        }
    }, [hasUploadsInProgress, retryNav]);

    // Automatically show modal when navigation is attempted during upload
    if (hasUploadsInProgress && retryNav && !showNavigationModal) {
        setShowNavigationModal(true);
    }

    return {
        hasUploadsInProgress,
        showNavigationModal,
        warningMessage,
        handleConfirmNavigation,
        handleCancelNavigation,
        showConfirmationModal,
    };
}
